import { MAX_PICKER_SCORE } from '../../scoreBounds'

/**
 * Turning a known final score into the three predictions worth testing against it.
 *
 * The point is the reveal: an exact score, a right result with the wrong scoreline, and a miss all
 * animate differently, and reaching each of them by hand costs a season reset per attempt.
 */
export type GoldenPick = 'exact' | 'close' | 'miss'

export interface Score {
  homeScore: number
  awayScore: number
}

const clamp = (value: number) => Math.max(0, Math.min(value, MAX_PICKER_SCORE))

export function goldenPickScore(final: Score, kind: GoldenPick): Score {
  const { homeScore: home, awayScore: away } = final
  if (kind === 'exact') return { homeScore: clamp(home), awayScore: clamp(away) }

  if (kind === 'close') {
    // Same winner, different scoreline: add a goal to whoever already won, and to both sides of a
    // draw so it stays one.
    if (home > away) return { homeScore: clamp(home + 1), awayScore: clamp(away) }
    if (home < away) return { homeScore: clamp(home), awayScore: clamp(away + 1) }
    return { homeScore: clamp(home + 1), awayScore: clamp(away + 1) }
  }

  // A miss has to flip the *outcome*, not just the numbers. Swapping does that for any decided
  // match; a draw has no swap, so the home side takes it.
  if (home === away) return { homeScore: clamp(home + 1), awayScore: clamp(away) }
  return { homeScore: clamp(away), awayScore: clamp(home) }
}
